import React, { useMemo, useRef, useState } from 'react';
import ManualEntryModal from './ManualEntryModal';

const round2 = (n) => Math.round(n * 100) / 100;

// Format a whole-dollar input for display; parse permissively (strip commas/$).
const parseDollars = (s) => String(s ?? '').replace(/[^0-9.]/g, '');

// Input panel: total $, optional retained cash / TDs $ held off the top, and
// the blend component rows (portfolio | AP List fund | direct asset class |
// manual), each with a % share splitting the invested remainder.
// "+ Manual" opens a modal (name + optional code + asset class + type) for
// anything outside the library; the result is session-only and flagged
// is_custom so every output marks it as adviser-classified.
// Portfolios are picked through a Series → Portfolio → Platform cascade,
// resolving to one vehicle code; a level with a single option auto-selects.
// Saved Portfolio Lab portfolios (Drummond staff only — `labPortfolios` is null
// for everyone else, which hides the button) are picked Client → Portfolio,
// with the client exactly as typed in Lab (Aaron 2026-10-13).
// Comparison mode (Aaron 2026-09-10) adds a Current Weight (%) column beside
// the target and a current $ field beside each retained item; both sides must
// foot to 100% independently.
const NO_CLIENT = '__no_client__';
const clientKeyOf = (p) => (p.client == null ? NO_CLIENT : p.client);
// 'YYYY-MM-DD' → 'dd/mm/yyyy' without a Date round-trip (no timezone shift).
const auDate = (iso) => (iso ? iso.split('-').reverse().join('/') : '');

const BlendBuilder = ({
  totalAmount, onTotalChange,
  retainedAmount, onRetainedChange,
  tdAmount, onTdChange,
  retainedCurrent, onRetainedCurrentChange,
  tdCurrent, onTdCurrentChange,
  components, onComponentsChange,
  vehicles, labPortfolios, funds, assetClassOptions, manualAssetClassOptions, loading,
  comparison, onCopyTargetToCurrent, onCopyCurrentToTarget,
}) => {
  const idRef = useRef(components.reduce((m, c) => Math.max(m, c.id), 0));
  const [fundSearch, setFundSearch] = useState({});   // component id -> search text
  const [cascade, setCascade] = useState({});         // component id -> {series, risk} (pre-resolution)
  const [labClient, setLabClient] = useState({});     // component id -> client key (pre-resolution)
  const [manualModal, setManualModal] = useState(null); // { id, initial } while the modal is open
  // Retained Cash and Term Deposits are separate fields/buttons but behave
  // identically (held off the top, defensive). Each field is hidden until added
  // via its button below the table; it appears beside the total investment field.
  const [showCash, setShowCash] = useState(() => !!retainedAmount || !!retainedCurrent);
  const [showTd, setShowTd] = useState(() => !!tdAmount || !!tdCurrent);

  const vehicleByCode = useMemo(
    () => Object.fromEntries((vehicles || []).map((v) => [v.code, v])),
    [vehicles],
  );

  // Cash and TDs are $ amounts held off the top (their own fields beside the
  // total) — the 100% check applies to the invested remainder only.
  const shareTotal = components.reduce((s, c) => s + (c.ref ? (c.share || 0) : 0), 0);
  const currentTotal = components.reduce((s, c) => s + (c.ref ? (c.current || 0) : 0), 0);
  const activeCount = components.filter((c) => c.ref).length;
  const sharesOk = Math.abs(shareTotal - 100) <= 0.01;
  const currentOk = Math.abs(currentTotal - 100) <= 0.01;
  const totalNum = parseFloat(totalAmount) || 0;
  const retainedTotal = (parseFloat(retainedAmount) || 0) + (parseFloat(tdAmount) || 0);
  const retainedCurrentTotal = (parseFloat(retainedCurrent) || 0) + (parseFloat(tdCurrent) || 0);
  const investedTarget = Math.max(0, totalNum - retainedTotal);
  const investedCurrent = Math.max(0, totalNum - retainedCurrentTotal);

  const usedPortfolioCodes = useMemo(
    () => new Set(components.filter((c) => c.type === 'portfolio' && c.ref).map((c) => c.ref)),
    [components],
  );
  const usedFundKeys = useMemo(
    () => new Set(components.filter((c) => c.type === 'fund' && c.ref)
      .map((c) => c.ref.apir_code || c.ref.tr_code)),
    [components],
  );
  const usedDirect = useMemo(
    () => new Set(components.filter((c) => c.type === 'direct' && c.ref).map((c) => c.ref)),
    [components],
  );
  const usedLabIds = useMemo(
    () => new Set(components.filter((c) => c.type === 'lab' && c.ref).map((c) => c.ref)),
    [components],
  );
  const labById = useMemo(
    () => Object.fromEntries((labPortfolios || []).map((p) => [String(p.id), p])),
    [labPortfolios],
  );
  // Client names exactly as typed in Lab, alphabetical; blank clients last.
  const labClientOptions = useMemo(() => {
    const named = [...new Set((labPortfolios || []).map((p) => p.client).filter((c) => c != null))]
      .sort((a, b) => a.localeCompare(b));
    const hasNone = (labPortfolios || []).some((p) => p.client == null);
    return hasNone ? [...named, NO_CLIENT] : named;
  }, [labPortfolios]);

  const fundsBySector = useMemo(() => {
    const g = new Map();
    (funds || []).forEach((f) => {
      const key = f.asset_class_group || 'Other';
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(f);
    });
    return [...g.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [funds]);

  const update = (id, patch) => onComponentsChange(
    components.map((c) => (c.id === id ? { ...c, ...patch } : c)),
  );
  const remove = (id) => onComponentsChange(components.filter((c) => c.id !== id));
  const add = (type) => {
    const id = ++idRef.current;
    onComponentsChange([...components, { id, type, ref: '', share: 0, current: 0 }]);
    if (type === 'manual') setManualModal({ id, initial: null });
  };
  // Even split fills one weight column ('share' = target, 'current' = current).
  const evenSplit = (field = 'share') => {
    const withRef = components.filter((c) => c.ref);
    if (withRef.length === 0) return;
    const each = round2(100 / withRef.length);
    const lastId = withRef[withRef.length - 1].id;
    let used = 0;
    onComponentsChange(components.map((c) => {
      if (!c.ref) return c;
      const v = c.id === lastId ? round2(100 - used) : each;
      used += each;
      return { ...c, [field]: v };
    }));
  };

  const closeManual = () => setManualModal(null);
  const cancelManual = () => {
    if (manualModal) {
      const row = components.find((c) => c.id === manualModal.id);
      // A fresh manual row that was never completed disappears on cancel.
      if (row && !row.ref) remove(manualModal.id);
    }
    closeManual();
  };
  const saveManual = (ref) => {
    if (manualModal) update(manualModal.id, { ref });
    closeManual();
  };

  // Series → Portfolio → Platform cascade (the middle level is the vehicle's
  // risk_label). Selecting a platform (or a portfolio with exactly one
  // platform) resolves the row to a vehicle code.
  const availableVehicles = useMemo(
    () => (vehicles || []).filter((v) => !usedPortfolioCodes.has(v.code)),
    [vehicles, usedPortfolioCodes],
  );

  const cascadeFor = (c) => {
    const v = c.ref ? vehicleByCode[c.ref] : null;
    if (v) return { series: v.suite, risk: v.risk_label };
    return cascade[c.id] || { series: '', risk: '' };
  };

  const seriesOptions = useMemo(() => {
    const s = [...new Set(availableVehicles.map((v) => v.suite || 'Other'))];
    return s.sort((a, b) => (a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)));
  }, [availableVehicles]);

  const renderPortfolioCascade = (c) => {
    const sel = cascadeFor(c);
    const pool = (vehicles || []).filter(
      (v) => v.code === c.ref || !usedPortfolioCodes.has(v.code),
    );
    const riskOptions = [...new Set(pool
      .filter((v) => (v.suite || 'Other') === sel.series)
      .map((v) => v.risk_label))]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const platformMatches = pool.filter(
      (v) => (v.suite || 'Other') === sel.series && v.risk_label === sel.risk,
    );

    const pickRisk = (risk) => {
      const matches = pool.filter(
        (v) => (v.suite || 'Other') === sel.series && v.risk_label === risk,
      );
      if (matches.length === 1) {
        // single platform — resolve straight to the vehicle
        setCascade((prev) => ({ ...prev, [c.id]: { series: sel.series, risk } }));
        update(c.id, { ref: matches[0].code });
      } else {
        setCascade((prev) => ({ ...prev, [c.id]: { series: sel.series, risk } }));
        if (c.ref) update(c.id, { ref: '' });
      }
    };

    return (
      <div className="alloc-cascade">
        <select
          className="form-select form-select-sm"
          value={sel.series}
          onChange={(e) => {
            setCascade((prev) => ({ ...prev, [c.id]: { series: e.target.value, risk: '' } }));
            if (c.ref) update(c.id, { ref: '' });
          }}
        >
          <option value="">Series…</option>
          {seriesOptions.concat(sel.series && !seriesOptions.includes(sel.series) ? [sel.series] : [])
            .map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          className="form-select form-select-sm"
          value={sel.risk}
          disabled={!sel.series}
          onChange={(e) => pickRisk(e.target.value)}
        >
          <option value="">Portfolio…</option>
          {riskOptions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select
          className="form-select form-select-sm"
          value={c.ref || ''}
          disabled={!sel.risk}
          onChange={(e) => update(c.id, { ref: e.target.value })}
        >
          <option value="">Platform…</option>
          {platformMatches.map((v) => (
            <option key={v.code} value={v.code}>{v.platform} ({v.code})</option>
          ))}
        </select>
      </div>
    );
  };

  // Client → Portfolio for a saved Lab portfolio. A client with exactly one
  // available portfolio resolves straight to it, like the vehicle cascade.
  const renderLabCascade = (c) => {
    const resolved = c.ref ? labById[c.ref] : null;
    const client = resolved ? clientKeyOf(resolved) : (labClient[c.id] ?? '');
    const options = (labPortfolios || []).filter((p) => clientKeyOf(p) === client
      && (String(p.id) === c.ref || !usedLabIds.has(String(p.id))));

    const pickClient = (key) => {
      setLabClient((prev) => ({ ...prev, [c.id]: key }));
      const avail = (labPortfolios || []).filter((p) => clientKeyOf(p) === key
        && !usedLabIds.has(String(p.id)));
      update(c.id, { ref: key && avail.length === 1 ? String(avail[0].id) : '' });
    };

    return (
      <div className="alloc-cascade">
        <select
          className="form-select form-select-sm"
          value={client}
          onChange={(e) => pickClient(e.target.value)}
        >
          <option value="">Client…</option>
          {labClientOptions.map((k) => (
            <option key={k} value={k}>{k === NO_CLIENT ? 'No client' : k}</option>
          ))}
        </select>
        <select
          className="form-select form-select-sm"
          value={c.ref || ''}
          disabled={!client}
          onChange={(e) => update(c.id, { ref: e.target.value })}
        >
          <option value="">Portfolio…</option>
          {options.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}{p.updated ? ` · updated ${auDate(p.updated)}` : ''}{p.geared ? ' · geared' : ''}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderRef = (c) => {
    if (c.type === 'portfolio') return renderPortfolioCascade(c);
    if (c.type === 'lab') return renderLabCascade(c);
    if (c.type === 'manual') {
      if (!c.ref) {
        return <span className="alloc-manual-pending">Complete the details in the window…</span>;
      }
      // A confirmed manual component is a read-only chip with an edit control
      // that reopens the modal prefilled.
      return (
        <div className="alloc-custom-fund">
          <span className="alloc-custom-fund-label">
            {c.ref.fund_name}
            {c.ref.apir_code ? ` (${c.ref.apir_code})` : ''}
            {' · '}{c.ref.asset_class_group}
            <span className="alloc-custom-tag">manual</span>
          </span>
          <button
            className="alloc-btn-remove"
            title="Edit"
            onClick={() => setManualModal({
              id: c.id,
              initial: {
                name: c.ref.fund_name || '',
                code: c.ref.apir_code || '',
                acg: c.ref.asset_class_group || '',
                type: c.ref.custom_type || '',
              },
            })}
          >
            ✎
          </button>
        </div>
      );
    }
    if (c.type === 'fund') {
      const q = (fundSearch[c.id] || '').trim().toLowerCase();
      const selectedKey = c.ref ? (c.ref.apir_code || c.ref.tr_code) : '';
      return (
        <div className="alloc-fund-picker">
          <input
            className="form-control form-control-sm"
            placeholder="Search fund name or APIR…"
            value={fundSearch[c.id] || ''}
            onChange={(e) => setFundSearch((prev) => ({ ...prev, [c.id]: e.target.value }))}
          />
          <select
            className="form-select form-select-sm"
            value={selectedKey}
            onChange={(e) => {
              const f = (funds || []).find(
                (x) => (x.apir_code || x.tr_code) === e.target.value,
              );
              update(c.id, { ref: f || '' });
            }}
          >
            <option value="">
              {funds.length ? 'Select a fund…' : 'No funds loaded — use + Manual'}
            </option>
            {fundsBySector.map(([sector, sectorFunds]) => {
              const opts = sectorFunds.filter((f) => {
                const key = f.apir_code || f.tr_code;
                if (key === selectedKey) return true;
                if (usedFundKeys.has(key)) return false;
                if (!q) return true;
                return (f.fund_name || '').toLowerCase().includes(q)
                  || (f.apir_code || '').toLowerCase().includes(q)
                  || (f.tr_code || '').toLowerCase().includes(q);
              });
              if (opts.length === 0) return null;
              return (
                <optgroup key={sector} label={sector}>
                  {opts.map((f) => (
                    <option key={f.apir_code || f.tr_code} value={f.apir_code || f.tr_code}>
                      {f.fund_name} ({f.apir_code || f.tr_code})
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>
      );
    }
    return (
      <select
        className="form-select form-select-sm"
        value={c.ref || ''}
        onChange={(e) => update(c.id, { ref: e.target.value })}
      >
        <option value="">Select an asset class…</option>
        {assetClassOptions
          .filter((ac) => ac === c.ref || !usedDirect.has(ac))
          .map((ac) => <option key={ac} value={ac}>{ac}</option>)}
      </select>
    );
  };

  const TYPE_LABELS = {
    portfolio: 'Portfolio', lab: 'Lab portfolio', fund: 'Fund', direct: 'Asset class',
  };
  const typeLabel = (c) => (c.type === 'manual'
    ? ((c.ref && c.ref.custom_type) || 'Manual')
    : TYPE_LABELS[c.type]);

  // One weight input; in comparison mode the implied $ sits under it.
  const renderWeight = (c, field, invested) => (
    <div className="alloc-weight">
      <input
        type="number"
        className="form-control form-control-sm"
        min={0}
        max={100}
        step={0.5}
        value={c[field] || ''}
        placeholder="0"
        onChange={(e) => update(c.id, {
          [field]: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)),
        })}
      />
      {comparison && invested > 0 && (
        <small className="alloc-implied">
          ${Math.round(((c[field] || 0) / 100) * invested).toLocaleString()}
        </small>
      )}
    </div>
  );

  // A retained $ item (cash or TDs): target field, plus a current field in
  // comparison mode. Removing clears both sides.
  const renderRetained = ({ id, labelText, title, target, onTarget, current, onCurrent, onRemove }) => (
    <div className="alloc-amount-field">
      <label htmlFor={id} title={title}>{labelText}</label>
      <div className="alloc-retained-field">
        {comparison && (
          <div className="alloc-side-input">
            <span className="alloc-side-caption">Current</span>
            <input
              id={`${id}-current`}
              className="form-control"
              inputMode="numeric"
              placeholder="$ amount"
              value={current}
              onChange={(e) => onCurrent(parseDollars(e.target.value))}
            />
          </div>
        )}
        <div className="alloc-side-input">
          {comparison && <span className="alloc-side-caption">Target</span>}
          <input
            id={id}
            className="form-control"
            inputMode="numeric"
            placeholder="$ amount"
            value={target}
            onChange={(e) => onTarget(parseDollars(e.target.value))}
          />
        </div>
        <button className="alloc-btn-remove" title={`Remove ${labelText.replace(' ($)', '').toLowerCase()}`} onClick={onRemove}>
          ×
        </button>
      </div>
    </div>
  );

  const investedLine = (prefix, invested, retained) => (
    <div>
      {prefix}Invested: ${invested.toLocaleString()}
      {' '}· Retained: ${retained.toLocaleString()}
      {totalNum > 0 && <> ({round2((retained / totalNum) * 100).toFixed(1)}%)</>}
    </div>
  );

  return (
    <div className="alloc-builder">
      <div className="alloc-amounts">
        <div className="alloc-amount-field">
          <label htmlFor="alloc-total">Total investment ($)</label>
          <input
            id="alloc-total"
            className="form-control"
            inputMode="numeric"
            placeholder="e.g. 2,000,000"
            value={totalAmount}
            onChange={(e) => onTotalChange(parseDollars(e.target.value))}
          />
        </div>
        {showCash && renderRetained({
          id: 'alloc-retained',
          labelText: 'Retained cash ($)',
          title: 'A dollar amount held back in cash — the components below split the remainder',
          target: retainedAmount,
          onTarget: onRetainedChange,
          current: retainedCurrent,
          onCurrent: onRetainedCurrentChange,
          onRemove: () => { onRetainedChange(''); onRetainedCurrentChange(''); setShowCash(false); },
        })}
        {showTd && renderRetained({
          id: 'alloc-td',
          labelText: 'Term deposits ($)',
          title: 'A dollar amount held back in term deposits — the components below split the remainder',
          target: tdAmount,
          onTarget: onTdChange,
          current: tdCurrent,
          onCurrent: onTdCurrentChange,
          onRemove: () => { onTdChange(''); onTdCurrentChange(''); setShowTd(false); },
        })}
        {totalAmount && (retainedTotal > 0 || (comparison && retainedCurrentTotal > 0)) && (
          <div className="alloc-investable">
            {comparison && investedLine('Current — ', investedCurrent, retainedCurrentTotal)}
            {investedLine(comparison ? 'Target — ' : '', investedTarget, retainedTotal)}
          </div>
        )}
      </div>

      <table className="alloc-components">
        <thead>
          <tr>
            <th className="type-col">Type</th>
            <th>Allocation component</th>
            {comparison && <th className="share-col">Current Weight (%)</th>}
            <th className="share-col">{comparison ? 'Target Weight (%)' : 'Weight (%)'}</th>
            <th className="x-col" />
          </tr>
        </thead>
        <tbody>
          {components.map((c) => (
            <tr key={c.id}>
              <td className="type-col">{typeLabel(c)}</td>
              <td>{renderRef(c)}</td>
              {comparison && (
                <td className="share-col">{renderWeight(c, 'current', investedCurrent)}</td>
              )}
              <td className="share-col">{renderWeight(c, 'share', investedTarget)}</td>
              <td className="x-col">
                {components.length > 1 && (
                  <button
                    className="alloc-btn-remove"
                    title="Remove"
                    onClick={() => remove(c.id)}
                  >
                    ×
                  </button>
                )}
              </td>
            </tr>
          ))}
          <tr className="alloc-share-total">
            <td />
            <td>Component total</td>
            {comparison && (
              <td className={`share-col ${currentOk ? 'ok' : 'off'}`}>{currentTotal.toFixed(1)}%</td>
            )}
            <td className={`share-col ${sharesOk ? 'ok' : 'off'}`}>{shareTotal.toFixed(1)}%</td>
            <td className="x-col" />
          </tr>
        </tbody>
      </table>

      <div className="alloc-builder-actions">
        <button className="btn btn-outline-secondary btn-sm" onClick={() => add('portfolio')}>
          + Portfolio
        </button>
        {labPortfolios && (
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => add('lab')}
            title="Drummond staff only — a saved Portfolio Lab portfolio, picked by client"
          >
            + Portfolio Lab Portfolio
          </button>
        )}
        <button className="btn btn-outline-secondary btn-sm" onClick={() => add('fund')}>
          + Fund
        </button>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => add('direct')}>
          + Asset class
        </button>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => add('manual')}
          title="Anything outside the library — name, optional code, asset class and type"
        >
          + Manual
        </button>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => setShowCash(true)}
          disabled={showCash}
          title="A dollar amount held back in cash — appears beside the total; the components above split the remainder"
        >
          + Retained Cash
        </button>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => setShowTd(true)}
          disabled={showTd}
          title="A dollar amount held back in term deposits — appears beside the total; the components above split the remainder"
        >
          + Term Deposits
        </button>
        {comparison ? (
          <>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => evenSplit('current')} disabled={activeCount === 0}>
              Even split current
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => evenSplit('share')} disabled={activeCount === 0}>
              Even split target
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={onCopyTargetToCurrent}
              disabled={activeCount === 0}
              title="Copy every target weight and retained amount into the current column"
            >
              Copy target → current
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={onCopyCurrentToTarget}
              disabled={activeCount === 0}
              title="Copy every current weight and retained amount into the target column"
            >
              Copy current → target
            </button>
          </>
        ) : (
          <button className="btn btn-outline-secondary btn-sm" onClick={() => evenSplit('share')} disabled={activeCount === 0}>
            Even split
          </button>
        )}
        {!sharesOk && activeCount > 0 && (
          <span className="alloc-share-warn">
            {comparison ? 'Target' : 'Component'} weights must total 100% (currently {shareTotal.toFixed(1)}%)
          </span>
        )}
        {comparison && !currentOk && activeCount > 0 && (
          <span className="alloc-share-warn">
            Current weights must total 100% (currently {currentTotal.toFixed(1)}%)
          </span>
        )}
        {loading && <span className="alloc-share-loading">loading targets…</span>}
      </div>

      {manualModal && (
        <ManualEntryModal
          initial={manualModal.initial}
          assetClassOptions={manualAssetClassOptions}
          onSave={saveManual}
          onCancel={cancelManual}
        />
      )}
    </div>
  );
};

export default BlendBuilder;
